import React, { FC, useCallback, useMemo, useState } from 'react';
import SaveFiltersPopup from './SaveFiltersPopup';
import { PAGINATION_PROPS, SORTING_PROPS } from '../../utils/DataConstants';
import SavedFilterViews from './SavedFilterViews';
import { compareStrings, filterKeys } from '../../utils/DataAccessUtils';
import { Button, Dropdown, Menu } from 'antd';
import { SaveOutlined, DownOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';

interface SaveFiltersActionsProps {
	preAppliedFilters: any;
	activeFIlters: any;
	activeSorter: any;
	userType: String;
	entityType: String;
	applyFiltersFromView: (filters, sorters) => void;
}

const SaveFiltersActions: FC<SaveFiltersActionsProps> = ({
	preAppliedFilters,
	activeFIlters,
	activeSorter,
	userType,
	entityType,
	applyFiltersFromView,
}): React.ReactElement => {
	const params = useParams<{ viewId?: string }>();

	const [saveFilterModalVisible, setSaveFilterModalVisible] = useState(false);
	const [savedViewsModalVisible, setSavedViewsModalVisible] = useState(false);
	const [isUpdateMode, setIsUpdateMode] = useState(false);

	const currentViewId = params.viewId;

	const showSaveFiltersModal = useCallback((updateMode: boolean = false) => {
		setIsUpdateMode(updateMode);
		setSaveFilterModalVisible(true);
	}, []);
	const hideSaveFilterModal = useCallback(() => {
		setSaveFilterModalVisible(false);
		setIsUpdateMode(false);
	}, []);
	const hideSavedViewsModal = useCallback(() => setSavedViewsModalVisible(false), []);
	const showSavedViewsModal = useCallback(() => setSavedViewsModalVisible(true), []);

	const applyFIlterFromView = useCallback(
		(filters) => {
			const withoutPagination = filterKeys(filters, PAGINATION_PROPS, false);
			applyFiltersFromView(
				filterKeys(withoutPagination, SORTING_PROPS, false),
				filterKeys(withoutPagination, SORTING_PROPS, true)
			);
			hideSavedViewsModal();
		},
		[hideSavedViewsModal, applyFiltersFromView]
	);

	const activeFiltersAndSorters = useMemo(
		() => ({
			...activeFIlters,
			...activeSorter,
		}),
		[activeFIlters, activeSorter]
	);

	const isViewMode = !!currentViewId;

	const saveViewMenu = useMemo(
		() => (
			<Menu>
				<Menu.Item key="update" onClick={() => showSaveFiltersModal(true)}>
					Update existing view
				</Menu.Item>
				<Menu.Item key="create" onClick={() => showSaveFiltersModal(false)}>
					Create new view
				</Menu.Item>
			</Menu>
		),
		[showSaveFiltersModal]
	);

	return (
		<>
			{isViewMode ? (
				<Dropdown overlay={saveViewMenu} trigger={['click']}>
					<Button size="large" icon={<SaveOutlined translate="no" />}>
						Save View <DownOutlined />
					</Button>
				</Dropdown>
			) : (
				<Button
					onClick={() => showSaveFiltersModal(false)}
					size="large"
					icon={<SaveOutlined translate="no" />}
				>
					Save View
				</Button>
			)}
			{compareStrings(entityType, 'work_orders') ? null : (
				<Button onClick={showSavedViewsModal} size="large" icon={<FolderOpenOutlined />}>
					Open View
				</Button>
			)}
			{saveFilterModalVisible && (
				<SaveFiltersPopup
					roleType={userType}
					entityType={entityType}
					filters={activeFiltersAndSorters}
					preAppliedFilters={preAppliedFilters}
					onCancel={hideSaveFilterModal}
					viewId={isUpdateMode ? currentViewId : undefined}
				/>
			)}

			{savedViewsModalVisible && (
				<SavedFilterViews
					roleType={userType}
					entityType={entityType}
					onApplyFilter={applyFIlterFromView}
					onCancel={hideSavedViewsModal}
				/>
			)}
		</>
	);
};

export default SaveFiltersActions;
